import type { SerializedEditorState } from "lexical";
import { reactive } from "vue";

import type { backendOld } from "~/api";
import { isDefined } from "~/shared/common";
import type { Comment, CommentReaction, CommentUpdate, Task } from "~/shared/types";
import { areLexicalStatesSame, isLexicalStateEmpty, makeDuid } from "~/utils/common";
import { makeStringComparator } from "~/utils/comparator";

import type { PiniaActionAdaptor, PiniaGetterAdaptor } from "../shared";
import type { DataStore } from ".";

const getPaginationKey = (taskDuid: string) => `comments:${taskDuid}`;

const updateLoadedComment = (
  dataStore: DataStore,
  comment: Comment,
  text: SerializedEditorState,
  publish: boolean
): CommentUpdate | undefined => {
  const loadedComment = comment;
  const textDidChange = !areLexicalStatesSame(loadedComment.text, text);
  if (!textDidChange && (!publish || !loadedComment.isDraft)) {
    return undefined;
  }

  const now = new Date().toISOString();
  const rootOrTaskDuid = loadedComment.rootDuid ?? loadedComment.taskDuid;
  loadedComment.text = text;
  const update: CommentUpdate = { duid: loadedComment.duid, text };
  if (publish && loadedComment.isDraft) {
    loadedComment.publishedAt = now;
    loadedComment.isDraft = false;
    update.publishedAt = loadedComment.publishedAt;
    dataStore._duidsWithoutDrafts.add(rootOrTaskDuid);
    dataStore.$addEntitiesToPaginationStates(getPaginationKey(loadedComment.taskDuid), [loadedComment]);
  } else if (!loadedComment.isDraft) {
    loadedComment.edited = true;
  }
  loadedComment.updatedAt = now;
  return update;
};

export type State = {
  _duidsToComments: Map<string, Comment>;
  _tasksDuidsWithoutComments: Set<string>;
  _duidsWithoutDrafts: Set<string>;
};

export const state: State = {
  _duidsToComments: new Map(),
  _tasksDuidsWithoutComments: new Set(),
  _duidsWithoutDrafts: new Set(),
};

export type Getters = {
  getCommentByDuid: (duid: string) => Promise<Comment | undefined>;
  getCommentByDuidIfLoaded: (duid: string) => Comment | undefined;
  getDraftCommentByTaskDuid: (taskDuid: string, rootDuid?: string) => Promise<Comment | undefined>;
  getDraftCommentByTaskDuidIfLoaded: (taskDuid: string, rootDuid?: string) => Comment | undefined;
  getLoadedCommentsByTaskDuidOrdered: (taskDuid: string) => Comment[];
  doesTaskHaveComments: (taskDuid: string) => Promise<boolean>;
  doesTaskHaveLoadedComments: (taskDuid: string) => boolean;
};

export type Actions = {
  loadComments: (
    ...args: Parameters<typeof backendOld.entityList.comment>
  ) => ReturnType<typeof backendOld.entityList.comment>;
  loadPaginatedCommentsForTaskDuid: (taskDuid: string) => Promise<number>;
  createComment: (
    task: Task,
    text: SerializedEditorState,
    partialComment?: Partial<Comment>,
    options?: { awaitBackend?: boolean }
  ) => Promise<Comment | undefined>;
  updateComment: (
    duid: string,
    text: SerializedEditorState,
    publish?: boolean,
    options?: { awaitBackend?: boolean }
  ) => Promise<Comment | undefined>;
  publishInitialCommentAgentView: (duid: string, text: SerializedEditorState) => Promise<Comment | undefined>;
  notifyCommentAgents: (commentDuid: string, agentDuids: string[]) => Promise<void>;
  deleteComment: (comment: Comment, options?: { awaitBackend?: boolean }) => Promise<void>;
  createCommentReaction: (duid: string, emoji: string, options?: { awaitBackend?: boolean }) => Promise<void>;
  deleteCommentReaction: (commentDuid: string, duid: string, options?: { awaitBackend?: boolean }) => Promise<void>;
  $createOrUpdateComment: (comment: Comment) => void;
  $deleteComment: (comment: Comment) => void;
};

const getters: PiniaGetterAdaptor<Getters, DataStore> = {
  getCommentByDuid() {
    return async (duid) => {
      const comment = this.getCommentByDuidIfLoaded(duid);
      if (comment) {
        return comment;
      }

      if (this.$isEntityDuidBlacklisted(duid)) {
        return undefined;
      }

      const commentFetched = await this.$batchers.entityListManager.getEntityByDuid("comments", duid);
      if (!commentFetched) {
        this.$blacklistEntityDuid("comments", duid);
        return undefined;
      }

      this.$createOrUpdateComment(commentFetched);
      return commentFetched;
    };
  },
  getCommentByDuidIfLoaded() {
    return (duid) => this._duidsToComments.get(duid);
  },
  getDraftCommentByTaskDuid() {
    const userStore = this.$useUserStore();
    return async (taskDuid, rootDuid) => {
      const comment = this.getDraftCommentByTaskDuidIfLoaded(taskDuid, rootDuid);
      if (comment) {
        return comment;
      }

      if (this._duidsWithoutDrafts.has(rootDuid ?? taskDuid)) {
        return undefined;
      }

      const { data } = await this.$backendOld.entityList.comment(
        { taskDuid, rootDuid: rootDuid ?? null, authorDuid: userStore.duid, isDraft: true },
        0,
        1
      );
      const commentFetched = data.results.length > 0 ? data.results[0] : undefined;
      if (!commentFetched) {
        this._duidsWithoutDrafts.add(rootDuid ?? taskDuid);
        return undefined;
      }

      this.$createOrUpdateComment(commentFetched);
      this._duidsWithoutDrafts.delete(rootDuid ?? taskDuid);
      return commentFetched;
    };
  },
  getDraftCommentByTaskDuidIfLoaded() {
    const userStore = this.$useUserStore();
    return (taskDuid, rootDuid) =>
      [...this._duidsToComments.values()].find(
        (e) =>
          e.taskDuid === taskDuid &&
          e.isDraft &&
          e.authorDuid === userStore.duid &&
          (rootDuid ? e.rootDuid === rootDuid : e.rootDuid === null)
      );
  },
  getLoadedCommentsByTaskDuidOrdered() {
    return (taskDuid) => {
      const paginationState = this.$getPaginationState(getPaginationKey(taskDuid), null);

      if (!paginationState) {
        return [];
      }

      const comments = [...paginationState.duids.values()]
        .map((e) => this.getCommentByDuidIfLoaded(e))
        .filter(isDefined);
      return comments;
    };
  },
  doesTaskHaveComments() {
    return async (taskDuid) => {
      if (this.doesTaskHaveLoadedComments(taskDuid)) {
        return true;
      }

      if (this._tasksDuidsWithoutComments.has(taskDuid)) {
        return false;
      }

      const commentFetched = await this.$batchers.taskCommentManager.getMostRecentCommentForTask(taskDuid);
      if (!commentFetched) {
        this._tasksDuidsWithoutComments.add(taskDuid);
        return false;
      }

      this.$createOrUpdateComment(commentFetched);
      this._tasksDuidsWithoutComments.delete(taskDuid);
      return true;
    };
  },
  doesTaskHaveLoadedComments() {
    return (taskDuid) => [...this._duidsToComments.values()].some((e) => e.taskDuid === taskDuid && !e.isDraft);
  },
};

const actions: PiniaActionAdaptor<Actions, DataStore> = {
  async loadComments(...args) {
    const response = await this.$backendOld.entityList.comment(...args);
    response.data.results
      .sort((a, b) => {
        // Sort by parent comments first because pagination comparator needs to find roots
        // Otherwise, child won't be added to the pagination state
        if (a.rootDuid === undefined && b.rootDuid !== undefined) {
          return -1;
        }
        if (a.rootDuid !== undefined && b.rootDuid === undefined) {
          return 1;
        }
        return 0;
      })
      .forEach((comment) => {
        if (this.getCommentByDuidIfLoaded(comment.duid)) {
          return;
        }
        this.$createOrUpdateComment(comment);
      });
    return response;
  },
  async loadPaginatedCommentsForTaskDuid(taskDuid) {
    const task = await this.getTaskByDuid(taskDuid);
    if (!task) {
      return 0;
    }

    const comparator = makeStringComparator((e: Comment) => {
      if (!e.rootDuid) {
        return e.publishedAt ?? e.updatedAt;
      }
      const rootComment = this.getCommentByDuidIfLoaded(e.rootDuid);
      return rootComment?.publishedAt ?? rootComment?.updatedAt ?? e.publishedAt ?? e.updatedAt;
    });

    // If the task has no comments, we don't need to load any
    const hasComments = await this.doesTaskHaveComments(taskDuid);
    if (!hasComments) {
      this.$createPaginationState(getPaginationKey(taskDuid), null, comparator);
      return 0;
    }

    const paginationState = this.$getPaginationState(getPaginationKey(taskDuid), null);
    const { data } = await this.loadComments(
      { taskDuid, isDraft: false, order: "hierarchical" },
      paginationState?.duids.size ?? 0
    );
    if (!paginationState) {
      this.$createPaginationState(getPaginationKey(taskDuid), null, comparator);
    }
    this.$addEntitiesToPaginationState(getPaginationKey(taskDuid), null, data.results, data.count);

    return data.count;
  },
  async createComment(task, text, partialComment, options = {}) {
    const { awaitBackend = false } = options;
    if (isLexicalStateEmpty(text)) {
      return undefined;
    }

    const userStore = this.$useUserStore();

    const now = new Date().toISOString();

    const comment: Comment = {
      duid: makeDuid(),
      createdAt: now,
      updatedAt: now,
      taskDuid: task.duid,
      rootDuid: null,
      authorDuid: userStore.duid,
      text,
      publishedAt: null,
      reactions: [],
      isDraft: true,
      edited: false,
      notifiedAgentDuids: [],
      ...partialComment,
    };

    this.$createOrUpdateComment(comment);

    const backendAction = (async () => {
      await this.$backend.comment.create({ ...comment });

      // TODO transactionify
      if (!task.subscriberDuids.includes(userStore.duid)) {
        await this.addSubscribers([task.duid], [userStore.duid], { noUndo: true, awaitBackend });
      }
    })();
    if (awaitBackend) {
      await backendAction;
    }

    return comment;
  },
  async updateComment(duid, text, publish = false, options = {}) {
    const { awaitBackend = false } = options;
    const comment = this.getCommentByDuidIfLoaded(duid);
    if (!comment) {
      return undefined;
    }

    if (isLexicalStateEmpty(text)) {
      return undefined;
    }

    const update = updateLoadedComment(this, comment, text, publish);
    if (!update) {
      return comment;
    }

    const backendAction = this.$backend.comment.update(update);
    if (awaitBackend) {
      await backendAction;
    }
    return comment;
  },
  async publishInitialCommentAgentView(duid, text) {
    const comment = this.getCommentByDuidIfLoaded(duid);
    if (!comment) {
      return undefined;
    }

    if (isLexicalStateEmpty(text)) {
      return undefined;
    }

    const update = updateLoadedComment(this, comment, text, true);
    if (!update) {
      return comment;
    }

    this.$backendOld.tasks.publishInitialCommentAgentView(duid, text);
    return comment;
  },
  async notifyCommentAgents(commentDuid, agentDuids) {
    const comment = this.getCommentByDuidIfLoaded(commentDuid);
    const newDuids = agentDuids.filter((duid) => !comment?.notifiedAgentDuids?.includes(duid));
    if (!comment || newDuids.length === 0) {
      return;
    }

    comment.notifiedAgentDuids = [...new Set([...comment.notifiedAgentDuids, ...newDuids])];
    await this.$backendOld.tasks.notifyCommentAgents(commentDuid, newDuids);
  },
  async deleteComment(comment, options = {}) {
    const { awaitBackend = false } = options;

    this.$deleteComment(comment);

    this.$addEntityDuidPendingDeletion(comment.duid);

    const backendAction = (async () => {
      await this.$backend.comment.delete(comment.duid);
      this.$removeEntityDuidPendingDeletion(comment.duid);
    })();
    if (awaitBackend) {
      await backendAction;
    }
  },
  async createCommentReaction(duid, emoji, options = {}) {
    const { awaitBackend = false } = options;

    const comment = this.getCommentByDuidIfLoaded(duid);
    if (!comment) {
      return;
    }

    const userStore = this.$useUserStore();

    const reaction = reactive<CommentReaction>({
      duid: makeDuid(),
      createdAt: new Date().toISOString(),
      authorDuid: userStore.duid,
      emoji,
    });
    comment.reactions.push(reaction);

    const task = this.getTaskByDuidIfLoaded(comment.taskDuid, { assertLoaded: true });
    const backendAction = (async () => {
      await this.$backend.commentReaction.create({ ...reaction, commentDuid: duid });

      // TODO transactionify
      if (task && !task.subscriberDuids.includes(userStore.duid)) {
        await this.addSubscribers([comment.taskDuid], [userStore.duid], { noUndo: true, awaitBackend });
      }
    })();
    if (awaitBackend) {
      await backendAction;
    }
  },
  async deleteCommentReaction(commentDuid, duid, options = {}) {
    const { awaitBackend = false } = options;

    const comment = this.getCommentByDuidIfLoaded(commentDuid);
    if (!comment) {
      return;
    }

    comment.reactions = comment.reactions.filter((e) => e.duid !== duid);

    const backendAction = this.$backend.commentReaction.delete(duid);
    if (awaitBackend) {
      await backendAction;
    }
  },
  $createOrUpdateComment(comment) {
    const currentComment = this.getCommentByDuidIfLoaded(comment.duid);
    if (this.$isEntityDuidPendingDeletion(comment.duid)) {
      return;
    }

    if (!currentComment) {
      this._duidsToComments.set(comment.duid, comment);
    } else {
      Object.assign(currentComment, comment);
    }

    this.$unblacklistEntityDuid(comment.duid);
    if (!comment.isDraft) {
      this.$addEntitiesToPaginationStates(getPaginationKey(comment.taskDuid), [comment]);
      this._tasksDuidsWithoutComments.delete(comment.taskDuid);
    } else {
      this._duidsWithoutDrafts.delete(comment.rootDuid ?? comment.taskDuid);
    }
  },
  $deleteComment(comment) {
    this._duidsToComments.delete(comment.duid);
    this.$removeEntitiesFromPaginationStates([comment]);
  },
};

export { actions, getters };
